import { Injectable } from '@angular/core';
import { AuthFacade } from '@facades/auth.facade';
import { OrderMessagesFacade } from '@facades/order-messages.facade';
import {
	BehaviorSubject,
	combineLatest,
	map,
	switchMap,
	startWith,
	interval,
	shareReplay,
} from 'rxjs';
import { IOrderMessage } from '../interfaces/orderMessage';

export interface IUnreadInfo {
	list: { orderId: number; orderNumber: number; total: number }[];
	total: number;
}

const REFRESH_INTERVAL = 60 * 1000;

@Injectable({
	providedIn: 'root',
})
export class UnreadOrderMsgsService {
	private readonly manualRefresh$ = new BehaviorSubject<void>(undefined);

	constructor(
		private authFacade: AuthFacade,
		private orderMessagesFacade: OrderMessagesFacade
	) {}

	private readonly trigger$ = combineLatest([
		this.authFacade.authState$,
		this.manualRefresh$.pipe(startWith(undefined)),
		interval(REFRESH_INTERVAL).pipe(startWith(0)),
	]);

	readonly unreadMessages$ = this.trigger$.pipe(
		switchMap(([auth]) => {
			const userId = auth.user?.userId;
			if (!userId) return [{ list: [], total: 0 }];

			return this.orderMessagesFacade
				.getUnreadOrderMessages()
				.pipe(map(response => this.groupUnread(response.result, userId)));
		}),
		shareReplay(1)
	);

	refresh() {
		this.manualRefresh$.next();
	}

	private groupUnread(
		messages: IOrderMessage[],
		currentUserId: number
	): IUnreadInfo {
		const filtered = messages.filter(msg => msg.userId !== currentUserId);

		const grouped: Record<number, { total: number; orderNumber: number }> = {};

		for (const msg of filtered) {
			if (!grouped[msg.orderId]) {
				grouped[msg.orderId] = {
					total: 0,
					orderNumber: msg.orderNumber ?? 0,
				};
			}
			grouped[msg.orderId].total++;
		}

		const list = Object.entries(grouped).map(([orderId, data]) => ({
			orderId: Number(orderId),
			orderNumber: data.orderNumber,
			total: data.total,
		}));

		const total = list.reduce((acc, i) => acc + i.total, 0);

		return { list, total };
	}
}
